import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuthApi } from "@/api/auth.api";
import { toast } from "sonner";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Mail,
  LockKeyhole,
  Eye,
  EyeOff,
  Info,
} from "lucide-react";

function base64Decode(str: string) {
  try {
    return decodeURIComponent(escape(window.atob(str)));
  } catch {
    return "";
  }
}

type VerifyStatus =
  | "pending"
  | "verified"
  | "setPassword"
  | "passwordSet"
  | "error";

const VerifyEmail: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<VerifyStatus>("pending");
  const [email, setEmail] = useState("");
  const [resetPasswordToken, setResetPasswordToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [formError, setFormError] = useState("");
  const [apiError, setApiError] = useState("");

  const PASSWORD_RULE_MESSAGE =
    "Password must be at least 7 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.";

  const passwordLongEnough = password.length >= 7;
  const passwordHasUppercase = /[A-Z]/.test(password);
  const passwordHasLowercase = /[a-z]/.test(password);
  const passwordHasDigit = /\d/.test(password);
  const passwordHasSpecial = /[^A-Za-z0-9]/.test(password);
  const isPasswordValid =
    passwordLongEnough &&
    passwordHasUppercase &&
    passwordHasLowercase &&
    passwordHasDigit &&
    passwordHasSpecial;
  const passwordsMatch =
    confirmPassword.length > 0 && password === confirmPassword;

  const isAlreadyVerified =
    apiError.toLowerCase().includes("already verified") ||
    apiError.toLowerCase().includes("already been created") ||
    apiError.toLowerCase().includes("account is active") ||
    apiError.toLowerCase().includes("already active");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    const code = params.get("code");
    if (!ref || !code) {
      setApiError("Invalid or missing verification link.");
      setStatus("error");
      return;
    }
    const decodedEmail = base64Decode(ref);
    const resetPasswordCode = base64Decode(code);
    if (!decodedEmail || !resetPasswordCode) {
      setApiError("Invalid verification link.");
      setStatus("error");
      return;
    }

    setEmail(decodedEmail);

    AuthApi.initiateResetPassword(decodedEmail, resetPasswordCode)
      .then((res: any) => {
        const data = res?.data || {};
        setEmail(data.email || decodedEmail);
        setResetPasswordToken(data.resetPasswordToken || "");
        setStatus("verified");
      })
      .catch((err: any) => {
        setApiError(
          err?.response?.data?.errorMessage ||
            err?.message ||
            "We could not verify your email address."
        );
        setStatus("error");
      });
  }, [location.search]);

  const handleResendInvitation = async () => {
    if (!email) {
      toast.error("Email address not found to resend invitation.");
      return;
    }
    setIsResending(true);
    try {
      const res: any = await AuthApi.resendInvitation(email);
      toast.success(
        res?.data?.message ||
          "Verification invitation resent successfully. Please check your email."
      );
    } catch (err: any) {
      toast.error(
        err?.response?.data?.errorMessage ||
          err?.message ||
          "Failed to resend invitation. Please try again."
      );
    } finally {
      setIsResending(false);
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!isPasswordValid) {
      setFormError(PASSWORD_RULE_MESSAGE);
      return;
    }
    if (password !== confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }
    if (!email || !resetPasswordToken) {
      setFormError(
        "Missing verification details. Please use the email link again."
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const res: any = await AuthApi.resetPassword(
        email,
        password,
        resetPasswordToken
      );
      const data = res?.data;

      if (data?.isSuccess === false) {
        const message =
          data?.statusMessage ||
          data?.errorMessage ||
          "Failed to set password. Please try again.";
        setFormError(String(message).replace(/^Error:\s*/i, ""));
        return;
      }

      setStatus("passwordSet");
    } catch (err: any) {
      const message =
        err?.response?.data?.statusMessage ||
        err?.response?.data?.errorMessage ||
        err?.message ||
        "Failed to set password. Please try again.";
      setFormError(String(message).replace(/^Error:\s*/i, ""));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-100 via-white to-blue-200 px-4 py-12 sm:px-6 lg:px-8 transition-colors duration-500">
      <div className="w-full max-w-md">
        <div className="bg-white shadow-2xl rounded-3xl p-10 flex flex-col items-center relative border border-blue-100">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4 shadow-sm">
            {status === "setPassword" || status === "passwordSet" ? (
              <LockKeyhole className="text-blue-600 w-8 h-8" />
            ) : (
              <Mail className="text-blue-600 w-8 h-8" />
            )}
          </div>
          <h2 className="text-center text-2xl font-extrabold mb-2 text-gray-800 tracking-tight">
            {status === "setPassword"
              ? "Set Password"
              : status === "passwordSet"
                ? "Password Set"
                : isAlreadyVerified
                  ? "Account Information"
                  : "Email Verification"}
          </h2>
          <p className="text-center text-gray-500 mb-8 text-base">
            {status === "pending" &&
              "We are verifying your email address. Please wait a moment."}
            {status === "verified" &&
              "Your email has been verified. Set a password to continue."}
            {status === "setPassword" &&
              "Create a password for your account."}
            {status === "passwordSet" &&
              "Your password has been set successfully."}
            {status === "error" &&
              (isAlreadyVerified
                ? "Your account is already active."
                : "We could not verify your email address.")}
          </p>

          {status === "pending" && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="animate-spin text-blue-500 w-8 h-8 mb-2" />
              <span className="text-blue-600 font-medium">
                Verifying your email...
              </span>
            </div>
          )}

          {status === "verified" && (
            <div className="flex flex-col items-center gap-4 py-4 w-full">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-2 shadow-sm">
                <CheckCircle2 className="text-green-600 w-8 h-8" />
              </div>
              <div className="text-green-700 font-semibold text-lg text-center">
                Email successfully verified!
              </div>
              <Button
                className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-lg py-2 text-base font-semibold shadow-md"
                onClick={() => setStatus("setPassword")}
              >
                Set Password
              </Button>
            </div>
          )}

          {status === "setPassword" && (
            <form onSubmit={handleSetPassword} className="space-y-4 w-full">
              <div className="space-y-1">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    autoComplete="new-password"
                    required
                    className="rounded-lg pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {password.length > 0 && !isPasswordValid && (
                  <p className="text-xs mt-1 text-red-500">
                    {PASSWORD_RULE_MESSAGE}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm password"
                    autoComplete="new-password"
                    required
                    className="rounded-lg pr-10"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label={
                      showConfirmPassword
                        ? "Hide confirm password"
                        : "Show confirm password"
                    }
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-xs mt-1 text-red-500">
                    Passwords do not match.
                  </p>
                )}
              </div>
              {formError && (
                <p className="text-sm text-red-600 font-medium">{formError}</p>
              )}
              <p className="text-xs text-gray-500">{PASSWORD_RULE_MESSAGE}</p>
              <Button
                type="submit"
                className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-lg py-2 text-base font-semibold shadow-md"
                disabled={
                  isSubmitting || !isPasswordValid || !passwordsMatch
                }
              >
                {isSubmitting ? "Setting password..." : "Set Password"}
              </Button>
            </form>
          )}

          {status === "passwordSet" && (
            <div className="flex flex-col items-center gap-4 py-4 w-full">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-2 shadow-sm">
                <CheckCircle2 className="text-green-600 w-8 h-8" />
              </div>
              <div className="text-green-700 font-semibold text-lg text-center">
                Password set successfully!
              </div>
              <Button
                className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-lg py-2 text-base font-semibold shadow-md"
                onClick={() => navigate("/login")}
              >
                Back to Login
              </Button>
            </div>
          )}

          {status === "error" && (
            <div className="flex flex-col items-center gap-4 py-8 w-full">
              {isAlreadyVerified ? (
                <>
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-2 shadow-sm">
                    <Info className="text-blue-600 w-8 h-8" />
                  </div>
                  <div className="text-gray-800 font-semibold text-lg text-center">
                    Account Already Active
                  </div>
                  {apiError && (
                    <p className="text-sm text-gray-600 text-center">{apiError}</p>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-2 shadow-sm">
                    <XCircle className="text-red-600 w-8 h-8" />
                  </div>
                  <div className="text-red-700 font-semibold text-lg text-center">
                    Email verification failed.
                  </div>
                  {apiError && (
                    <p className="text-sm text-red-600 text-center">{apiError}</p>
                  )}
                </>
              )}
              {!isAlreadyVerified && email && (
                <Button
                  className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-lg py-2 text-base font-semibold shadow-md flex items-center justify-center"
                  onClick={handleResendInvitation}
                  disabled={isResending}
                >
                  {isResending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Resending Invitation...
                    </>
                  ) : (
                    "Resend Invitation"
                  )}
                </Button>
              )}
              {(isAlreadyVerified || !email) && (
                <Button
                  className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-lg py-2 text-base font-semibold shadow-md"
                  onClick={() => navigate("/login")}
                >
                  Back to Login
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
